<template>
  <v-card
    class="kpi-card"
    :class="[`kpi-card--${color}`, { 'kpi-card--with-delta': hasDelta }]"
    elevation="1"
    height="120"
  >
    <v-card-text class="pa-4 d-flex flex-column h-100 justify-space-between">
      <!-- Label -->
      <div class="kpi-label text-caption text--secondary font-weight-medium">
        {{ label }}
      </div>

      <!-- Main Value -->
      <div class="kpi-value-container d-flex align-end justify-space-between">
        <div class="kpi-value">
          <span class="text-h4 font-weight-bold" :class="`${color}--text`">
            <template v-if="valueIsNumeric">
              <number
                class="animated-number"
                :from="lastNumber"
                :to="numericValue"
                :duration="0.6"
                easing="Power4.easeOut"
                :format="numberFormatFn"
              />
            </template>
            <template v-else>
              {{ formattedValue }}
            </template>
          </span>
          <span v-if="suffix" class="kpi-suffix text-body-2 text--secondary ml-1">
            {{ suffix }}
          </span>
        </div>

        <!-- Delta/Trend Indicator -->
        <div v-if="hasDelta" class="kpi-delta">
          <v-chip
            small
            :color="deltaColor"
            text-color="white"
            class="kpi-delta-chip"
          >
            <v-icon small class="mr-1">
              {{ deltaIcon }}
            </v-icon>
            {{ Math.abs(delta) }}{{ deltaUnit }}
          </v-chip>
        </div>
      </div>

      <!-- Optional Status Pill -->
      <div v-if="hasPill" class="kpi-pill mt-2">
        <v-chip
          x-small
          :color="pillColor"
          text-color="white"
          pill
        >
          <v-icon x-small class="mr-1">
            {{ pillIcon }}
          </v-icon>
          {{ pillText }}
        </v-chip>
      </div>
    </v-card-text>
  </v-card>
</template>

<script>
export default {
  name: 'KpiCard',
  data() {
    return {
      lastNumber: 0,
      animationTimer: null,
      animationDurationMs: 600
    };
  },
  mounted() {
    if (this.valueIsNumeric) {
      this.lastNumber = this.numericValue || 0;
    }
  },
  watch: {
    numericValue(newVal, oldVal) {
      // Schedule updating from-value after the animation completes
      if (this.animationTimer) {
        clearTimeout(this.animationTimer);
        this.animationTimer = null;
      }
      if (typeof newVal === 'number' && !isNaN(newVal)) {
        this.animationTimer = setTimeout(() => {
          this.lastNumber = newVal;
          this.animationTimer = null;
        }, this.animationDurationMs);
      }
    }
  },
  beforeDestroy() {
    if (this.animationTimer) {
      clearTimeout(this.animationTimer);
      this.animationTimer = null;
    }
  },
  props: {
    label: {
      type: String,
      required: true
    },
    value: {
      type: [Number, String],
      required: true
    },
    suffix: {
      type: String,
      default: ''
    },
    color: {
      type: String,
      default: 'primary',
      // validator: value => ['primary', 'blue', 'green', 'orange', 'red', 'purple', 'teal', 'grey'].includes(value)
    },
    delta: {
      type: Number,
      default: null
    },
    deltaUnit: {
      type: String,
      default: '%'
    },
    pill: {
      type: Object,
      default: null
    },
    decimals: {
      type: Number,
      default: 0
    }
  },
  computed: {
    valueIsNumeric() {
      return typeof this.value === 'number' || (!isNaN(Number(this.value)) && this.value !== '' && this.value !== null && this.value !== undefined);
    },
    numericValue() {
      if (typeof this.value === 'number') return this.value;
      const n = Number(this.value);
      return isNaN(n) ? null : n;
    },
    formattedValue() {
      if (typeof this.value === 'number') {
        return this.value.toLocaleString('fr-FR', {
          minimumFractionDigits: this.decimals,
          maximumFractionDigits: this.decimals
        });
      }
      return this.value;
    },
    numberFormatFn() {
      const decimals = this.decimals;
      return (val) => {
        const num = typeof val === 'number' ? val : Number(val);
        if (isNaN(num)) return '';
        // If integers and large, apply k suffix as before
        if (decimals === 0 && Math.abs(num) >= 1000) {
          const k = num / 1000;
          const s = k.toFixed(1).replace(/\.0$/, '') + 'k';
          return s;
        }
        // Locale formatting with fixed decimals
        return num.toLocaleString('fr-FR', {
          minimumFractionDigits: decimals,
          maximumFractionDigits: decimals
        });
      };
    },
    hasDelta() {
      return this.delta !== null && this.delta !== undefined;
    },
    deltaColor() {
      if (this.delta > 0) return 'success';
      if (this.delta < 0) return 'error';
      return 'grey';
    },
    deltaIcon() {
      if (this.delta > 0) return 'mdi-arrow-up';
      if (this.delta < 0) return 'mdi-arrow-down';
      return 'mdi-minus';
    },
    hasPill() {
      return this.pill && this.pill.text;
    },
    pillColor() {
      return this.pill?.color || 'primary';
    },
    pillText() {
      return this.pill?.text || '';
    },
    pillIcon() {
      return this.pill?.icon || this.getStatusIcon(this.pillText);
    }
  },
  methods: {
    getStatusIcon(status) {
      const iconMap = {
        'SUCCESS': 'mdi-check-circle',
        'FAILED': 'mdi-close-circle',
        'RUNNING': 'mdi-play-circle',
        'PENDING': 'mdi-clock-outline',
        'CANCELLED': 'mdi-stop-circle'
      };
      return iconMap[status?.toUpperCase()] || 'mdi-circle';
    }
  }
};
</script>

<style lang="scss">
.kpi-card {
  border-radius: 8px;
  transition: all 0.2s ease-in-out;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15) !important;
  }

  // Color variants
  &--blue {
    border-left: 4px solid #2563EB;
  }

  &--green {
    border-left: 4px solid #16A34A;
  }

  &--orange {
    border-left: 4px solid #D97706;
  }

  &--red {
    border-left: 4px solid #DC2626;
  }

  &--purple {
    border-left: 4px solid #7C3AED;
  }

  &--teal {
    border-left: 4px solid #0D9488;
  }

  &--grey {
    border-left: 4px solid #6B7280;
  }

  &--primary {
    border-left: 4px solid var(--v-primary-base);
  }
}

.kpi-label {
  line-height: 1.2;
  margin-bottom: auto;
}

.kpi-value-container {
  flex-grow: 1;
  align-items: flex-end;
}

.kpi-value {
  .kpi-suffix {
    opacity: 0.7;
  }
}

.kpi-delta {
  .kpi-delta-chip {
    height: 24px;
    font-size: 0.75rem;
  }
}

.kpi-pill {
  margin-top: auto;

  .v-chip {
    height: 20px;
    font-size: 0.7rem;
  }
}

// Responsive adjustments
@media (max-width: 768px) {
  .kpi-card {
    height: 96px !important;

    .kpi-value {
      .text-h4 {
        font-size: 1.5rem !important;
      }
    }
  }
}
</style>